/**
 * LuxonLeadAssist Embeddable Widget
 * 
 * Standalone chat widget that can be embedded on any website via <script> tag.
 */

import { buildWidgetConfig, buildWidgetStyles, type WidgetBrandingConfig } from "./branding";

interface ConversationState {
  conversationId: string | null;
  sessionId: string;
  messages: Message[];
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// Widget class
class LuxonLeadAssistWidget {
  private config: WidgetBrandingConfig;
  private state: ConversationState;
  private isOpen: boolean = false;
  private leadSubmitted: boolean = false;
  
  private button: HTMLButtonElement | null = null;
  private panel: HTMLDivElement | null = null;

  constructor(config: WidgetBrandingConfig) {
    if (!config.clientId) {
      throw new Error('LuxonLeadAssist Widget: clientId is required');
    }

    this.config = buildWidgetConfig(
      {
        clientId: config.clientId,
        apiBaseUrl: config.apiBaseUrl || ((import.meta as any).env?.VITE_WIDGET_API_BASE_URL || 'http://localhost:8601'),
        position: config.position || 'bottom-right',
        primaryColor: config.primaryColor || '#4F46E5',
        botName: config.botName || 'Assistant',
        taglineText: config.taglineText,
        welcomeMessage: config.welcomeMessage || 'Hi! How can I help you today?',
        launcherColor: config.launcherColor,
        launcherIcon: config.launcherIcon,
        launcherText: config.launcherText,
        launcherTeaser: config.launcherTeaser,
        launcherSize: config.launcherSize,
        headerBackgroundColor: config.headerBackgroundColor,
        headerTextColor: config.headerTextColor,
        panelBackgroundColor: config.panelBackgroundColor || '#ffffff',
        cardRadius: config.cardRadius || 12,
        panelWidth: config.panelWidth || 380,
        panelHeight: config.panelHeight || 600,
        aiBubbleBg: config.aiBubbleBg || '#f3f4f6',
        aiTextColor: config.aiTextColor || '#1f2937',
        userBubbleBg: config.userBubbleBg,
        userTextColor: config.userTextColor || '#ffffff',
        bubbleRadius: config.bubbleRadius || 12,
        bubblePadding: config.bubblePadding || 10,
        bubbleFontSize: config.bubbleFontSize || 14,
        inputBackgroundColor: config.inputBackgroundColor || '#ffffff',
        inputTextColor: config.inputTextColor || '#111827',
        inputBorderColor: config.inputBorderColor || '#d1d5db',
        inputPlaceholder: config.inputPlaceholder || 'Type your message...',
        inputPlaceholderColor: config.inputPlaceholderColor || '#9ca3af',
        sendButtonBgColor: config.sendButtonBgColor,
        sendButtonTextColor: config.sendButtonTextColor || '#ffffff',
        fontFamily: config.fontFamily || "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        showBubbles: config.showBubbles,
        showThumbsUp: config.showThumbsUp,
        showThumbsDown: config.showThumbsDown,
        feedbackHint: config.feedbackHint,
        positionMode: config.positionMode || 'fixed',
        preview: config.preview,
        container: config.container,
        brandingOverride: config.brandingOverride,
      },
      config.brandingOverride || null
    );

    // Initialize state
    this.state = {
      conversationId: null,
      sessionId: this.generateSessionId(),
      messages: [],
    };

    // Initialize UI
    this.init();
  }

  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private async init() {
    await this.loadBranding();
    this.injectStyles();
    this.createWidget();
  }

  private async loadBranding() {
    if (this.config.preview) {
      return;
    }
    try {
      const response = await fetch(`${this.config.apiBaseUrl}/api/admin/settings/public/branding`);
      if (!response.ok) return;
      const data = await response.json();
      const branding = data?.branding || data || {};
      this.config = buildWidgetConfig(this.config, branding);
    } catch (error) {
      console.warn('Failed to load widget branding', error);
    }
  }

  private injectStyles() {
    const styleId = 'luxon-widget-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = buildWidgetStyles(this.config);
    document.head.appendChild(style);
  }

  private createWidget() {
    // Create floating button
    this.button = document.createElement('button');
    this.button.className = 'luxon-widget-button';
    this.button.innerHTML = this.config.launcherIcon || '💬';
    this.button.title = 'Chat with us';
    this.button.onclick = () => this.toggle();

    if (this.config.launcherTeaser) {
      const teaser = document.createElement('div');
      teaser.className = 'luxon-widget-teaser';
      teaser.textContent = this.config.launcherTeaser;
      this.getContainer().appendChild(teaser);
    }

    // Create chat panel
    this.panel = document.createElement('div');
    this.panel.className = 'luxon-widget-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'luxon-widget-header';
    header.innerHTML = `
      <div class="luxon-widget-header-title">
        <span>${this.config.botName}</span>
        ${this.config.taglineText ? `<span class="luxon-widget-header-subtitle">${this.config.taglineText}</span>` : ''}
      </div>
      <button class="luxon-widget-close">×</button>
    `;
    const closeBtn = header.querySelector('.luxon-widget-close') as HTMLButtonElement;
    closeBtn.onclick = () => this.close();

    // Messages container
    const messages = document.createElement('div');
    messages.className = 'luxon-widget-messages';
    messages.id = 'luxon-widget-messages';

    // Welcome message
    this.addMessage('assistant', this.config.welcomeMessage || 'Hi! How can I help you today?');

    // Input container
    const inputContainer = document.createElement('div');
    inputContainer.className = 'luxon-widget-input-container';
    inputContainer.innerHTML = `
      <input
        type="text"
        class="luxon-widget-input"
        placeholder="${this.config.inputPlaceholder}"
        id="luxon-widget-input"
      />
      <button class="luxon-widget-send" id="luxon-widget-send">Send</button>
    `;

    const input = inputContainer.querySelector('#luxon-widget-input') as HTMLInputElement;
    const sendBtn = inputContainer.querySelector('#luxon-widget-send') as HTMLButtonElement;

    input.onkeypress = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage(input.value);
      }
    };

    sendBtn.onclick = () => this.sendMessage(input.value);

    // Assemble panel
    this.panel.appendChild(header);
    this.panel.appendChild(messages);
    this.panel.appendChild(inputContainer);

    // Add to DOM
    this.getContainer().appendChild(this.button);
    this.getContainer().appendChild(this.panel);
  }

  private getContainer(): HTMLElement {
    if (this.config.container) return this.config.container;

    // Use an isolated zero-size fixed root so widget DOM mutations
    // cannot affect document/body layout or trigger host-page scroll.
    const existingRoot = document.getElementById('luxon-widget-root');
    if (existingRoot) return existingRoot;

    const root = document.createElement('div');
    root.id = 'luxon-widget-root';
    root.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'width:0',
      'height:0',
      'overflow:visible',
      'z-index:999999',
      'pointer-events:none',
    ].join(';');
    document.body.appendChild(root);
    return root;
  }

  private toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  private async open() {
    this.isOpen = true;
    this.panel?.classList.add('open');

    // Start conversation on first open
    if (!this.state.conversationId) {
      await this.startConversation();
    }
  }

  private close() {
    this.isOpen = false;
    this.panel?.classList.remove('open');
  }

  private async startConversation() {
    try {
      const pageUrl = window.location.href;
      const referrer = document.referrer || '';
      const response = await fetch(`${this.config.apiBaseUrl}/api/chat/v2/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: this.config.clientId,
          session_id: this.state.sessionId,
          visitor_id: this.generateVisitorId(),
          page_url: pageUrl,
          referrer,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      this.state.conversationId = data.conversation_id;
      console.log('Conversation started:', data.conversation_id);
    } catch (error) {
      console.error('Failed to start conversation:', error);
      this.addMessage('assistant', 'Sorry, I\'m having trouble connecting. Please try again.');
    }
  }

  private generateVisitorId(): string {
    // Simple visitor fingerprint (could be enhanced)
    return `visitor-${Date.now()}`;
  }

  private async sendMessage(text: string) {
    const input = document.getElementById('luxon-widget-input') as HTMLInputElement;
    const sendBtn = document.getElementById('luxon-widget-send') as HTMLButtonElement;

    if (!text.trim()) return;

    // Clear input and lock it (readonly keeps focus; disabled triggers browser scroll-to-focus on re-enable)
    input.value = '';
    input.setAttribute('readonly', '');
    input.style.opacity = '0.6';
    sendBtn.disabled = true;

    // Remove any un-submitted lead form so a re-prompt can appear fresh after this message
    if (!this.leadSubmitted) {
      const existingForm = document.getElementById('luxon-lead-form');
      if (existingForm) existingForm.remove();
    }

    // Add user message to UI
    this.addMessage('user', text);

    // Show loading indicator
    const loadingId = this.addMessage('assistant', 'Typing...');

    try {
      if (this.config.preview) {
        this.removeMessage(loadingId);
        this.addMessage('assistant', 'Preview mode: chat replies are disabled.', true);
        return;
      }
      // Ensure conversation is started
      if (!this.state.conversationId) {
        await this.startConversation();
      }

      // Send message
      const response = await fetch(`${this.config.apiBaseUrl}/api/chat/v2/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: this.config.clientId,
          conversation_id: this.state.conversationId,
          message: text,
          page_url: window.location.href,
          page_title: document.title || '',
          page_h1: (document.querySelector('h1') as HTMLElement | null)?.innerText?.trim() || '',
          page_type: 'unknown',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Remove loading indicator
      this.removeMessage(loadingId);

    // Add AI response
    this.addMessage('assistant', data.ai_response, true);

      // Handle lead capture if needed
      if (data.action === 'show_lead_form' && !this.leadSubmitted) {
        this.showLeadForm(data.trigger_reason);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      this.removeMessage(loadingId);
      this.addMessage('assistant', 'Sorry, something went wrong. Please try again.');
    } finally {
      // Re-enable input using readonly removal (not disabled toggle) to avoid browser scroll-to-focus
      input.removeAttribute('readonly');
      input.style.opacity = '';
      sendBtn.disabled = false;
    }
  }

  private addMessage(role: 'user' | 'assistant', content: string, allowFeedback: boolean = false): string {
    const messageId = `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const messagesContainer = document.getElementById('luxon-widget-messages');
    
    if (!messagesContainer) return messageId;

    const messageDiv = document.createElement('div');
    messageDiv.className = `luxon-widget-message ${role}`;
    messageDiv.id = messageId;
    messageDiv.textContent = content;

    if (
      role === 'assistant' &&
      allowFeedback &&
      (this.config.showThumbsUp || this.config.showThumbsDown)
    ) {
      const feedback = document.createElement('div');
      feedback.className = 'luxon-widget-feedback';
      if (this.config.feedbackHint) {
        const hint = document.createElement('span');
        hint.textContent = this.config.feedbackHint;
        feedback.appendChild(hint);
      }
      if (this.config.showThumbsUp) {
        const btnUp = document.createElement('button');
        btnUp.textContent = '👍';
        feedback.appendChild(btnUp);
      }
      if (this.config.showThumbsDown) {
        const btnDown = document.createElement('button');
        btnDown.textContent = '👎';
        feedback.appendChild(btnDown);
      }
      messageDiv.appendChild(feedback);
    }

    messagesContainer.appendChild(messageDiv);
    // No auto-scroll — user scrolls manually.

    // Store in state
    this.state.messages.push({
      id: messageId,
      role,
      content,
      timestamp: new Date(),
    });

    return messageId;
  }

  private removeMessage(messageId: string) {
    const messageDiv = document.getElementById(messageId);
    if (messageDiv) {
      messageDiv.remove();
    }
    this.state.messages = this.state.messages.filter(m => m.id !== messageId);
  }

  private normalizeTriggerReason(reason?: string): 'message_count' | 'buying_intent' | 'low_confidence' | 'user_request' | 'manual' {
    if (!reason) return 'manual';

    const raw = reason.trim();
    const canonical = ['message_count', 'buying_intent', 'low_confidence', 'user_request', 'manual'] as const;
    if ((canonical as readonly string[]).includes(raw)) {
      return raw as 'message_count' | 'buying_intent' | 'low_confidence' | 'user_request' | 'manual';
    }

    const normalized = raw.toLowerCase().replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();
    if (normalized.startsWith('message count trigger') || normalized === 'message count') return 'message_count';
    if (normalized.includes('buying intent')) return 'buying_intent';
    if (normalized.includes('low confidence')) return 'low_confidence';
    if (['user request', 'user requested contact', 'contact requested'].includes(normalized)) return 'user_request';
    if (normalized.includes('let us get in touch') || normalized.includes('get in touch')) return 'user_request';
    if (normalized === 'manual' || normalized === 'manual trigger') return 'manual';

    return 'manual';
  }

  private showLeadForm(reason?: string) {
    const triggerReason = this.normalizeTriggerReason(reason);
    const messagesContainer = document.getElementById('luxon-widget-messages');
    if (!messagesContainer) return;

    // Prevent duplicate forms
    if (document.getElementById('luxon-lead-form')) return;

    const primary = this.config.primaryColor || '#4F46E5';
    const formId = 'luxon-lead-form';

    const formDiv = document.createElement('div');
    formDiv.className = 'luxon-widget-message assistant';
    formDiv.id = formId;
    formDiv.innerHTML = `
      <div style="padding:4px 0;">
        <strong style="font-size:14px;">Let us get in touch with you</strong>
        <div style="margin:4px 0 10px; font-size:12px; color:#6b7280;">
          ${triggerReason === 'buying_intent' ? 'Looks like you\'re ready to move forward — let us reach out.'
            : triggerReason === 'user_request' ? 'Happy to connect. Fill in your details below.'
            : 'Fill in your details and we\'ll be in touch.'}
        </div>
        <div id="luxon-lead-error" style="display:none;margin-bottom:8px;padding:6px 8px;background:#fee2e2;border-radius:6px;font-size:12px;color:#b91c1c;"></div>
        <div style="margin-bottom:8px;">
          <input id="luxon-lead-name" type="text" placeholder="Your name *"
            style="width:100%;box-sizing:border-box;padding:7px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:13px;outline:none;" />
        </div>
        <div style="margin-bottom:8px;">
          <input id="luxon-lead-email" type="email" placeholder="Email address *"
            style="width:100%;box-sizing:border-box;padding:7px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:13px;outline:none;" />
        </div>
        <div style="margin-bottom:12px;">
          <input id="luxon-lead-phone" type="tel" placeholder="Phone (optional)"
            style="width:100%;box-sizing:border-box;padding:7px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:13px;outline:none;" />
        </div>
        <button id="luxon-lead-submit"
          style="width:100%;padding:8px;background:${primary};color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;">
          Send my details
        </button>
      </div>
    `;

    messagesContainer.appendChild(formDiv);

    const submitBtn = document.getElementById('luxon-lead-submit') as HTMLButtonElement;
    const errorDiv = document.getElementById('luxon-lead-error') as HTMLDivElement;

    submitBtn.addEventListener('click', async () => {
      const name = (document.getElementById('luxon-lead-name') as HTMLInputElement).value.trim();
      const email = (document.getElementById('luxon-lead-email') as HTMLInputElement).value.trim();
      const phone = (document.getElementById('luxon-lead-phone') as HTMLInputElement).value.trim();

      // Validate
      errorDiv.style.display = 'none';
      if (!name) { errorDiv.textContent = 'Please enter your name.'; errorDiv.style.display = 'block'; return; }
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errorDiv.textContent = 'Please enter a valid email address.'; errorDiv.style.display = 'block'; return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending…';

      try {
        const resp = await fetch(`${this.config.apiBaseUrl}/api/leads/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: this.config.clientId,
            conversation_id: this.state.conversationId || '',
            name,
            email,
            phone: phone || undefined,
            trigger_reason: triggerReason,
            page_url: window.location.href,
            referrer: document.referrer || undefined,
          }),
        });

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || `Server error ${resp.status}`);
        }

        // Replace form with success message
        formDiv.innerHTML = `
          <div style="padding:8px 0;text-align:center;">
            <div style="font-size:22px;margin-bottom:6px;">✅</div>
            <strong style="font-size:14px;">Got it, thanks ${name}!</strong>
            <div style="margin-top:4px;font-size:12px;color:#6b7280;">We'll be in touch at ${email}.</div>
          </div>
        `;
        // Mark as submitted so the form never appears again in this conversation
        this.leadSubmitted = true;
      } catch (err: any) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Send my details';
        errorDiv.textContent = err.message || 'Something went wrong. Please try again.';
        errorDiv.style.display = 'block';
      }
    });
  }
}

// Global API
declare global {
  interface Window {
    LuxonLeadAssistWidget: {
      init: (config: WidgetBrandingConfig) => LuxonLeadAssistWidget;
    };
  }
}

window.LuxonLeadAssistWidget = {
  init: (config: WidgetBrandingConfig) => {
    return new LuxonLeadAssistWidget(config);
  },
};

export { LuxonLeadAssistWidget, type WidgetBrandingConfig };
